import clamp from "lodash/clamp";
import debounce from "lodash/debounce";
import { v7 as uuid } from "uuid";
import { Interruptible } from "@lib/utils/interruptible";
import { posthog, AnalyticsEvent } from "@src/analytics";
import { LoadedRegion } from "./AudioPlayer";
import { AudioStorage } from "./AudioStorage";
import { EngineStorage } from "./EngineStorage";
import { CommandLog } from "./CommandLog";
import { AudioInputManager } from "./AudioInputManager";
import { AudioInput } from "./AudioInput";
import { LegacyRegionLoader } from "./LegacyRegionLoader";
import { RegionLoader } from "./RegionLoader";
import { LegacyPlaybackBackend } from "./backends/LegacyPlaybackBackend";
import {
  PlaybackBackend,
  PlaybackBackendDebugState,
  PlaybackBackendKind,
  StreamingBackendFallbackReason,
} from "./backends/PlaybackBackend";
import { StreamingPlaybackBackend } from "./backends/StreamingPlaybackBackend";
import {
  getStreamingBackendSelectionDecision,
  supportsStreamingPlayback,
} from "./backends/shouldUseStreamingBackend";
import {
  AudioEngineState,
  AudioEngineTickState,
  AudioEngineLoadOptions,
  TrackState,
  TrackInfo,
  RegionData,
  RegionState,
  DEFAULT_TRACK_STATE_VALUES,
  PersistedEngineState,
  PersistedPendingRegion,
  WaveformData,
  AudioInputDevice,
  PendingRecording,
  PunchInResult,
  CreatedRegion,
} from "./types";
import { sliceAudioBuffer } from "./utils/sliceAudioBuffer";
import { audioBufferToWav } from "./utils/audioBufferToWav";
import { combineMasterWaveform } from "./combineMasterWaveform";

export type { CommandLogEntry } from "./CommandLog";

type Listener = () => void;

export interface AudioEngineOptions {
  /** Enable command logging for devtools. Default: false */
  enableDevTools?: boolean;
}

interface AudioEngineBackendDebugState extends PlaybackBackendDebugState {
  activeBackend: PlaybackBackendKind | null;
}

interface AudioEngineRegionDebugState {
  channelCount: number | null;
  codec: string | null;
  hasAudioBlob: boolean;
  hasAudioBuffer: boolean;
  hasWaveformKey: boolean;
}

interface AudioEngineDebugState {
  backend: AudioEngineBackendDebugState;
  regions: Record<string, AudioEngineRegionDebugState>;
}

/** Debounce delay for persisting state changes (ms) */
const PERSIST_DEBOUNCE_MS = 500;

/**
 * AudioEngine encapsulates all audio playback state and logic.
 *
 * It wraps a playback backend and provides:
 * - Transport controls (play, pause, stop, seek)
 * - Track state management (solo, mute, gain)
 * - Timing loop for current timestamp updates
 * - State subscription via subscribe/getSnapshot pattern (for React useSyncExternalStore)
 *
 * @example
 * ```ts
 * const engine = new AudioEngine();
 * await engine.load({ audioContext, trackData, duration });
 *
 * // Subscribe to state changes
 * const unsubscribe = engine.subscribe(() => {
 *   console.log(engine.getSnapshot());
 * });
 *
 * engine.play();
 * ```
 */
export class AudioEngine {
  private playbackBackend: PlaybackBackend | null = null;
  private audioContext: AudioContext | null = null;
  private listeners: Set<Listener> = new Set();
  private tickListeners: Set<Listener> = new Set();
  private animationFrameId: number | null = null;
  private seekInterruptible: Interruptible<void> | null = null;

  // Internal state
  private _state: AudioEngineState;
  private _tickState: AudioEngineTickState;
  private _debugState: AudioEngineDebugState;
  private destroyed: boolean = false;

  /** Whether this engine instance has been destroyed. */
  get isDestroyed(): boolean {
    return this.destroyed;
  }

  // Transport timing
  private playingFrom: number = 0;
  private playStartGeneration = 0;
  private startedAt: number | null = null; // audioContext.currentTime when playback started
  private resumeOnSeekComplete: boolean = false;

  // Persistence and storage
  private audioStorage: AudioStorage | null = null;
  private engineStorage: EngineStorage | null = null;
  private songId: string | null = null;
  private debouncedPersist: ReturnType<typeof debounce> | null = null;

  // Command log for devtools (only instantiated when enabled)
  readonly commandLog: CommandLog | null = null;

  // Master waveform cache
  private _cachedMasterWaveform: WaveformData | null = null;
  private _masterWaveformDeps: {
    waveforms: Record<string, WaveformData>;
    regions: Record<string, RegionState>;
    duration: number;
  } | null = null;

  // Recording and input monitoring
  private inputManager: AudioInputManager = new AudioInputManager();
  private audioInput: AudioInput = new AudioInput();
  private pendingBlobs: Map<string, Blob> = new Map();
  private pendingRegionsToRestore: Array<{
    trackId: string;
    regionId: string;
    audioBuffer: AudioBuffer;
    position: number;
  }> = [];

  constructor(options?: AudioEngineOptions) {
    this._state = this.createInitialState();
    this._tickState = this.createInitialTickState();
    this._debugState = this.createInitialDebugState();

    if (options?.enableDevTools) {
      this.commandLog = new CommandLog();
    }
  }

  // ============================================================================
  // State Management
  // ============================================================================

  private createInitialState(): AudioEngineState {
    return {
      ready: false,
      playing: false,
      seeking: false,
      duration: 0,
      masterGain: 0.8,
      tracks: {},
      regions: {},
      waveforms: {},
      recording: false,
      recordingStartPosition: null,
      selectedInputDeviceId: null,
      inputMonitoring: false,
      inputAudioMonitoring: false,
      inputChannelCount: null,
      selectedInputChannel: 'stereo',
      recordingTargetTrackId: null,
      deletedRegionIds: [],
      playbackRate: 1.0,
      transpose: 0,
      loadingProgress: {
        total: 0,
        loaded: 0,
        error: null,
      },
    };
  }

  private createInitialTickState(): AudioEngineTickState {
    return {
      currentTimestamp: 0,
      inputLevel: 0,
      inputLevelRight: 0,
      recordingWaveform: null,
    };
  }

  private createInitialDebugState(): AudioEngineDebugState {
    return {
      backend: {
        activeBackend: null,
        trackBuffers: [],
      },
      regions: {},
    };
  }

  private getDefaultRegionDebugState(): AudioEngineRegionDebugState {
    return {
      channelCount: null,
      codec: null,
      hasAudioBlob: false,
      hasAudioBuffer: false,
      hasWaveformKey: false,
    };
  }

  /**
   * Get the current state snapshot.
   * This is a stable reference that only changes when state changes.
   */
  getSnapshot = (): AudioEngineState => {
    return this._state;
  };

  /**
   * Subscribe to state changes.
   * Returns an unsubscribe function.
   */
  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  private notifyListeners(): void {
    // Snapshot the listeners to prevent iteration issues if a listener
    // modifies the Set (e.g., by subscribing/unsubscribing during callback)
    Array.from(this.listeners).forEach((listener) => listener());
  }

  // ============================================================================
  // Tick State Management (high-frequency updates)
  // ============================================================================

  /**
   * Get the current tick state snapshot.
   * This is a stable reference that only changes when tick state changes.
   */
  getTickSnapshot = (): AudioEngineTickState => {
    return this._tickState;
  };

  /**
   * Get a stable snapshot of active-backend and region metadata for devtools.
   */
  getDebugState = (): AudioEngineDebugState => {
    const backendDebugState = this.playbackBackend?.getDebugState?.();

    if (!backendDebugState) {
      return this._debugState;
    }

    return {
      ...this._debugState,
      backend: {
        ...this._debugState.backend,
        ...backendDebugState,
      },
    };
  };

  /**
   * Subscribe to tick state changes.
   * Returns an unsubscribe function.
   */
  subscribeTick = (listener: Listener): (() => void) => {
    this.tickListeners.add(listener);
    return () => {
      this.tickListeners.delete(listener);
    };
  };

  private notifyTickListeners(): void {
    this.tickListeners.forEach((listener) => listener());
  }

  private setTickState(updates: Partial<AudioEngineTickState>): void {
    this._tickState = { ...this._tickState, ...updates };
    this.notifyTickListeners();
  }

  /**
   * Log a command with state diff tracking.
   * Captures state before, executes the action, captures state after, and passes to CommandLog.
   */
  private logCommand<T>(
    command: string,
    args: Record<string, unknown> | undefined,
    action: () => T
  ): T {
    if (!this.commandLog) {
      return action();
    }

    const before = this._state;
    const result = action();
    const after = this._state;

    this.commandLog.add(command, args, before, after);
    return result;
  }

  private setState(updates: Partial<AudioEngineState>): void {
    // Create new state object to ensure referential inequality for React
    this._state = { ...this._state, ...updates };
    this.notifyListeners();
  }

  private mergeRegionDebugState(
    updates: Record<string, Partial<AudioEngineRegionDebugState>>
  ): void {
    const nextRegions = { ...this._debugState.regions };

    for (const [regionId, patch] of Object.entries(updates)) {
      nextRegions[regionId] = {
        ...this.getDefaultRegionDebugState(),
        ...nextRegions[regionId],
        ...patch,
      };
    }

    this._debugState = {
      ...this._debugState,
      regions: nextRegions,
    };
  }

  private primeRegionDebugState(regionData: RegionData[]): void {
    const regionDebugEntries = Object.fromEntries(
      regionData.map((region) => [
        region.id,
        {
          channelCount: region.channelCount ?? null,
          codec: region.codec ?? null,
          hasAudioBlob: region.audioBlob instanceof Blob,
          hasAudioBuffer: false,
          hasWaveformKey: region.waveformKey !== null,
        } satisfies AudioEngineRegionDebugState,
      ])
    );

    this._debugState = {
      ...this.createInitialDebugState(),
      regions: regionDebugEntries,
    };
  }

  private updateLoadedRegionDebugState(regions: LoadedRegion[]): void {
    this.mergeRegionDebugState(
      Object.fromEntries(
        regions.map((region) => {
          const updates: Partial<AudioEngineRegionDebugState> = {
            hasAudioBlob: region.audioBlob instanceof Blob,
            hasAudioBuffer: region.audioBuffer !== undefined,
          };

          if (region.audioBuffer) {
            updates.channelCount = region.audioBuffer.numberOfChannels;
          }

          return [region.id, updates] as const;
        })
      )
    );
  }

  private getPlaybackBackend(): PlaybackBackend {
    if (!this.playbackBackend) {
      throw new Error("AudioEngine not loaded");
    }

    return this.playbackBackend;
  }

  private createPlaybackBackend(kind: PlaybackBackendKind): PlaybackBackend {
    return kind === "streaming"
      ? new StreamingPlaybackBackend()
      : new LegacyPlaybackBackend();
  }

  private async loadPlaybackBackend(
    kind: PlaybackBackendKind,
    options: {
      audioContext: AudioContext;
      tracks: TrackInfo[];
      regions: LoadedRegion[];
    }
  ): Promise<{
    backend: PlaybackBackend;
    fallbackReason: StreamingBackendFallbackReason | null;
  }> {
    const backend = this.createPlaybackBackend(kind);

    try {
      await backend.load({
        audioContext: options.audioContext,
        tracks: options.tracks,
        regions: options.regions,
        sampleRate: 48000,
      });

      return { backend, fallbackReason: null };
    } catch (error) {
      backend.destroy();

      if (kind !== "streaming") {
        throw error;
      }

      this.captureStreamingFatalError(error, "init");

      const fallbackRegions = await this.ensureRegionsDecodedForLegacy(
        options.audioContext,
        options.regions
      );
      this.updateLoadedRegionDebugState(fallbackRegions);
      const fallbackBackend = this.createPlaybackBackend("legacy");
      await fallbackBackend.load({
        audioContext: options.audioContext,
        tracks: options.tracks,
        regions: fallbackRegions,
        sampleRate: 48000,
      });

      return {
        backend: fallbackBackend,
        fallbackReason: "streaming_init_failed",
      };
    }
  }

  private async ensureRegionsDecodedForLegacy(
    audioContext: AudioContext,
    regions: LoadedRegion[]
  ): Promise<LoadedRegion[]> {
    return Promise.all(regions.map(async (region) => {
      if (region.audioBuffer) {
        return region;
      }

      if (!region.audioBlob) {
        throw new Error(`Legacy fallback requires encoded audio data for region ${region.id}`);
      }

      const arrayBuffer = await region.audioBlob.arrayBuffer();
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

      return {
        ...region,
        audioBuffer,
        duration: audioBuffer.duration,
      };
    }));
  }

  private capturePlaybackBackendSelection(params: {
    fallbackReason: StreamingBackendFallbackReason | null;
    requestedBackend: PlaybackBackendKind;
    selectedBackend: PlaybackBackendKind;
  }): void {
    posthog.capture(AnalyticsEvent.AUDIO_BACKEND_SELECTED, {
      fallbackReason: params.fallbackReason,
      requestedBackend: params.requestedBackend,
      selectedBackend: params.selectedBackend,
      songId: this.songId,
    });
  }

  private captureStreamingFatalError(error: unknown, stage: "init"): void {
    posthog.capture(AnalyticsEvent.AUDIO_STREAMING_FATAL_ERROR, {
      message: error instanceof Error ? error.message : String(error),
      songId: this.songId,
      stage,
    });
  }

  private invalidatePendingPlayStart(): void {
    this.playStartGeneration++;
  }

  private startPlaybackFromCurrentPosition(): Promise<void> {
    const playbackBackend = this.getPlaybackBackend();
    const playStartGeneration = ++this.playStartGeneration;

    const finalizePlaybackStart = () => {
      if (
        playStartGeneration !== this.playStartGeneration ||
        this.destroyed ||
        !this._state.playing
      ) {
        return;
      }

      this.startedAt = this.audioContext?.currentTime ?? null;
      this.startTimestampLoop();
    };

    let backendPlayPromise: Promise<void>;

    try {
      backendPlayPromise = playbackBackend.play(this.playingFrom);
    } catch (error) {
      if (playStartGeneration === this.playStartGeneration && !this.destroyed) {
        this.startedAt = null;
        this.stopTimestampLoop();
        if (this._state.playing) {
          this.setState({ playing: false });
        }
      }

      throw error;
    }

    const playPromise = backendPlayPromise
      .then(() => {
        if (playbackBackend.kind === "streaming") {
          finalizePlaybackStart();
        }
      })
      .catch((error) => {
        if (playStartGeneration === this.playStartGeneration && !this.destroyed) {
          this.startedAt = null;
          this.stopTimestampLoop();
          if (this._state.playing) {
            this.setState({ playing: false });
          }
        }

        throw error;
      });

    if (playbackBackend.kind === "legacy") {
      finalizePlaybackStart();
    }

    playPromise.catch(() => {});
    return playPromise;
  }

  private setTrackState(trackId: string, updates: Partial<TrackState>): void {
    const currentTrack = this._state.tracks[trackId];
    if (!currentTrack) {
      throw new Error(`Track ${trackId} not found`);
    }
    this._state = {
      ...this._state,
      tracks: {
        ...this._state.tracks,
        [trackId]: { ...currentTrack, ...updates },
      },
    };
    this.notifyListeners();
  }

  private setRegionState(regionId: string, updates: Partial<RegionState>): void {
    const currentRegion = this._state.regions[regionId];
    if (!currentRegion) {
      throw new Error(`Region ${regionId} not found`);
    }
    this._state = {
      ...this._state,
      regions: {
        ...this._state.regions,
        [regionId]: { ...currentRegion, ...updates },
      },
    };
    this.notifyListeners();
  }

  // ============================================================================
  // Lifecycle
  // ============================================================================

  /**
   * Load audio data into the engine.
   * Must be called before play/pause/seek operations.
   *
   * @param options.songId - Required. Used for storage key prefixing and persistence.
   * @throws Error if songId is not provided
   */
  async load(options: AudioEngineLoadOptions): Promise<void> {
    const {
      audioContext,
      tracks: trackInfos,
      regions: regionData,
      duration,
      songId,
      downloadKey,
      streamingAudioEngineEnabled = false,
    } = options;

    if (!songId) {
      throw new Error("songId is required for AudioEngine.load()");
    }

    // Initialize storage and context
    this.audioContext = audioContext;
    this.songId = songId;
    this.audioStorage = new AudioStorage(songId, downloadKey);
    this.engineStorage = new EngineStorage(songId);
    this.debouncedPersist = debounce(() => this.persistState(), PERSIST_DEBOUNCE_MS);
    this.primeRegionDebugState(regionData);

    const persistedState = this.engineStorage
      ? await this.engineStorage.getState()
      : null;

    // Build initial state from track/region data and persisted state
    const initialState = await this.buildInitialState(
      audioContext,
      trackInfos,
      regionData,
      duration,
      persistedState
    );
    if (this.destroyed) return;
    this.setState(initialState);

    const browserSupportsStreamingPlayback = supportsStreamingPlayback(audioContext);
    const backendSelection = getStreamingBackendSelectionDecision({
      streamingAudioEngineEnabled,
      browserSupportsStreamingPlayback,
      regions: regionData,
      pendingRegions: persistedState?.pendingRegions,
    });

    // Load audio regions
    const { loadedRegions, loadedWaveforms } = await this.loadRegions(
      audioContext,
      regionData,
      { useStreamingLoader: backendSelection.useStreamingBackend }
    );
    this.updateLoadedRegionDebugState(loadedRegions);
    if (this.destroyed) return;

    // If any regions failed to load, stop here and keep ready: false
    // so the LoadingModal stays open and shows the error state
    if (this._state.loadingProgress.error) {
      return;
    }

    const requestedBackend: PlaybackBackendKind = backendSelection.useStreamingBackend
      ? "streaming"
      : "legacy";
    const { backend, fallbackReason } = await this.loadPlaybackBackend(
      requestedBackend,
      {
        audioContext,
        tracks: trackInfos,
        regions: loadedRegions,
      }
    );

    if (this.destroyed) {
      backend.destroy();
      return;
    }

    this.playbackBackend = backend;
    this._debugState = {
      ...this._debugState,
      backend: {
        activeBackend: backend.kind,
        trackBuffers: [],
      },
    };

    // Batch update all waveforms
    this.setState({ waveforms: { ...this._state.waveforms, ...loadedWaveforms } });

    // Restore any pending regions from previous session
    this.restorePendingRegionsToPlayer();

    // Initialize gains, playback rate, transpose, and audio input
    this.initializePlaybackSettings();
    await AudioInput.registerWorklet(audioContext);
    if (this.destroyed) return;

    // Subscribe to ready state changes and mark as ready
    this.subscribeToReadyState();
    this.setState({ ready: true });
    this.capturePlaybackBackendSelection({
      fallbackReason: fallbackReason ?? backendSelection.fallbackReason,
      requestedBackend,
      selectedBackend: backend.kind,
    });
  }

  /**
   * Build initial engine state from track/region data and persisted state.
   */
  private async buildInitialState(
    audioContext: AudioContext,
    trackInfos: TrackInfo[],
    regionData: RegionData[],
    duration: number,
    persistedState: PersistedEngineState | null
  ): Promise<Partial<AudioEngineState>> {
    // Initialize track states
    const tracks: Record<string, TrackState> = {};
    trackInfos.forEach((track) => {
      tracks[track.id] = {
        id: track.id,
        label: track.label,
        order: track.order,
        ...DEFAULT_TRACK_STATE_VALUES,
      };
    });

    // Initialize region states
    const regions: Record<string, RegionState> = {};
    regionData.forEach((region) => {
      regions[region.id] = {
        id: region.id,
        trackId: region.trackId,
        position: region.position,
        duration: region.duration ?? 0,
        pendingUpload: false,
      };
    });

    // Apply persisted values
    let masterGain = this._state.masterGain;
    let selectedInputDeviceId = this._state.selectedInputDeviceId;
    let selectedInputChannel = this._state.selectedInputChannel;
    let inputAudioMonitoring = this._state.inputAudioMonitoring;
    let deletedRegionIds: string[] = [];
    let waveforms: Record<string, WaveformData> = {};

    if (persistedState) {
      masterGain = persistedState.masterGain;

      if (persistedState.selectedInputDeviceId !== undefined) {
        selectedInputDeviceId = persistedState.selectedInputDeviceId;
      }
      if (persistedState.selectedInputChannel !== undefined) {
        selectedInputChannel = persistedState.selectedInputChannel;
      }
      if (persistedState.inputAudioMonitoring !== undefined) {
        inputAudioMonitoring = persistedState.inputAudioMonitoring;
      }
      if (persistedState.deletedRegionIds) {
        deletedRegionIds = persistedState.deletedRegionIds;
      }
      if (persistedState.pendingWaveforms) {
        waveforms = persistedState.pendingWaveforms;
      }

      // Apply persisted track states (only for tracks that exist)
      Object.entries(persistedState.tracks ?? {}).forEach(([trackId, savedState]) => {
        if (tracks[trackId]) {
          tracks[trackId] = {
            ...tracks[trackId],
            solo: savedState.solo,
            muted: savedState.muted,
            gain: savedState.gain,
          };
        }
      });

      // Restore pending regions
      if (persistedState.pendingRegions && persistedState.pendingRegions.length > 0) {
        await this.restorePendingRegions(audioContext, persistedState.pendingRegions, tracks, regions, waveforms);
      }
    }

    // Recompute disabled state for all tracks based on solo/mute
    const hasGlobalSolo = Object.values(tracks).some((c) => c.solo);
    Object.keys(tracks).forEach((id) => {
      const track = tracks[id];
      tracks[id] = {
        ...track,
        disabled: track.muted || (hasGlobalSolo && !track.solo),
      };
    });

    return {
      duration,
      tracks,
      regions,
      waveforms,
      masterGain,
      selectedInputDeviceId,
      selectedInputChannel,
      inputAudioMonitoring,
      deletedRegionIds,
      loadingProgress: { total: regionData.length, loaded: 0, error: null },
    };
  }

  /**
   * Load all regions using the appropriate loader for the active backend.
   *
   * Streaming: uses `RegionLoader` which pre-decodes MP3 to interleaved PCM
   *   and derives duration from the encoded container.
   * Legacy: uses `LegacyRegionLoader` which fully decodes via `decodeAudioData()`.
   */
  private async loadRegions(
    audioContext: AudioContext,
    regionData: RegionData[],
    options?: { useStreamingLoader?: boolean }
  ): Promise<{ loadedRegions: LoadedRegion[]; loadedWaveforms: Record<string, WaveformData> }> {
    const onProgress = (loaded: number, total: number) => {
      this.setState({ loadingProgress: { total, loaded, error: null } });
    };
    const onError = (regionId: string, error: Error) => {
      console.error(`Failed to load region ${regionId}:`, error);
      this.setState({
        loadingProgress: { ...this._state.loadingProgress, error },
      });
    };

    if (options?.useStreamingLoader) {
      return this.loadRegionsStreaming(audioContext, regionData, onProgress, onError);
    }

    return this.loadRegionsLegacy(audioContext, regionData, onProgress, onError);
  }

  private async loadRegionsStreaming(
    audioContext: AudioContext,
    regionData: RegionData[],
    onProgress: (loaded: number, total: number) => void,
    onError: (regionId: string, error: Error) => void,
  ): Promise<{ loadedRegions: LoadedRegion[]; loadedWaveforms: Record<string, WaveformData> }> {
    const regionLoader = new RegionLoader(this.audioStorage!);
    const loadResults = await regionLoader.loadAll(
      regionData.map((r) => ({
        audioBlob: r.audioBlob,
        codec: r.codec,
        duration: r.duration,
        id: r.id,
        key: r.key,
        waveformKey: r.waveformKey,
      })),
      onProgress,
      onError,
    );

    const loadedWaveforms: Record<string, WaveformData> = {};
    for (const result of loadResults) {
      loadedWaveforms[result.id] = result.waveform;
    }

    const loadedRegions: LoadedRegion[] = loadResults.map((result) => {
      const regionInfo = regionData.find((r) => r.id === result.id)!;
      return {
        id: result.id,
        trackId: regionInfo.trackId,
        audioBlob: result.audioBlob ?? regionInfo.audioBlob,
        codec: regionInfo.codec,
        duration: result.duration ?? regionInfo.duration,
        position: regionInfo.position,
        preDecodedPcm: result.preDecodedPcm,
      };
    });

    return { loadedRegions, loadedWaveforms };
  }

  private async loadRegionsLegacy(
    audioContext: AudioContext,
    regionData: RegionData[],
    onProgress: (loaded: number, total: number) => void,
    onError: (regionId: string, error: Error) => void,
  ): Promise<{ loadedRegions: LoadedRegion[]; loadedWaveforms: Record<string, WaveformData> }> {
    const regionLoader = new LegacyRegionLoader(audioContext, this.audioStorage!);
    const loadResults = await regionLoader.loadAll(
      regionData.map((r) => ({
        audioBlob: r.audioBlob,
        id: r.id,
        key: r.key,
        waveformKey: r.waveformKey,
      })),
      onProgress,
      onError,
    );

    const loadedWaveforms: Record<string, WaveformData> = {};
    for (const result of loadResults) {
      loadedWaveforms[result.id] = result.waveform;
    }

    const loadedRegions: LoadedRegion[] = loadResults.map((result) => {
      const regionInfo = regionData.find((r) => r.id === result.id)!;
      return {
        id: result.id,
        trackId: regionInfo.trackId,
        audioBlob: result.audioBlob ?? regionInfo.audioBlob,
        audioBuffer: result.audioBuffer,
        codec: regionInfo.codec,
        duration: result.duration ?? regionInfo.duration,
        position: regionInfo.position,
      };
    });

    return { loadedRegions, loadedWaveforms };
  }

  /**
   * Restore pending regions from previous session to the active playback backend.
   */
  private restorePendingRegionsToPlayer(): void {
    const playbackBackend = this.getPlaybackBackend();

    for (const pending of this.pendingRegionsToRestore) {
      playbackBackend.addRegionToTrack(pending.trackId, {
        id: pending.regionId,
        trackId: pending.trackId,
        audioBuffer: pending.audioBuffer,
        codec: null,
        duration: pending.audioBuffer.duration,
        position: pending.position,
      });
    }

    this.pendingRegionsToRestore = [];
  }

  /**
   * Initialize playback backend gain/rate/transpose settings from current state.
   */
  private initializePlaybackSettings(): void {
    const playbackBackend = this.getPlaybackBackend();

    playbackBackend.setMasterGain(this._state.masterGain);
    playbackBackend.setTranspose(this._state.transpose);
    playbackBackend.setPlaybackRate(this._state.playbackRate);
    Object.entries(this._state.tracks).forEach(([id, trackState]) => {
      this.applyTrackGain(id, trackState);
    });
  }

  /**
   * Subscribe to playback backend ready state changes to sync engine ready state.
   */
  private subscribeToReadyState(): void {
    this.getPlaybackBackend().onReadyStateChange((readyPromise) => {
      // Pause playback if currently playing (audio is being loaded)
      if (this._state.playing) {
        this.pause();
      }

      // Set ready to false while we wait
      this.setState({ ready: false });

      // Check if ready promise exists (may not in tests/mocks)
      if (!readyPromise || typeof readyPromise.then !== 'function') {
        this.setState({ ready: true });
        return;
      }

      // Wait for the backend to be ready, then update state
      readyPromise
        .then(() => {
          if (!this.destroyed) {
            this.setState({ ready: true });
          }
        })
        .catch(() => {
          // If loading fails, still set ready to true to avoid blocking the UI
          if (!this.destroyed) {
            this.setState({ ready: true });
          }
        });
    });
  }

  /**
   * Clean up resources.
   * Call this when done with the engine.
   */
  destroy(): void {
    // Mark as destroyed to prevent pending callbacks from running
    this.destroyed = true;

    // Cancel any active recording
    if (this._state.recording) {
      this.cancelRecording();
    }

    // Stop timestamp loop
    this.stopTimestampLoop();

    // Stop playback
    if (this._state.playing) {
      this.pause();
    }

    // Flush any pending persist operations
    if (this.debouncedPersist) {
      this.debouncedPersist.flush();
      this.debouncedPersist = null;
    }

    // Destroy the active playback backend
    this.playbackBackend?.destroy();
    this.playbackBackend = null;

    // Stop input monitoring
    this.audioInput.stop();

    // Destroy input manager
    this.inputManager.destroy();

    // Clear pending blobs
    this.pendingBlobs.clear();

    // Clear listeners
    this.listeners.clear();
    this.tickListeners.clear();

    // Clear persistence and storage references
    this.audioStorage = null;
    this.engineStorage = null;
    this.songId = null;

    // Reset state
    this._state = this.createInitialState();
    this._tickState = this.createInitialTickState();
    this._debugState = this.createInitialDebugState();
  }

  // ============================================================================
  // Transport Controls
  // ============================================================================

  /**
   * Start playback from current position.
   */
  play(): Promise<void> {
    if (this._state.playing) return Promise.resolve();
    if (!this._state.ready) throw new Error("Engine not loaded");

    return this.logCommand("play", { from: this.playingFrom, deferred: this._state.seeking }, () => {
      // Resume audio context if suspended (browser autoplay policy)
      if (this.audioContext?.state === "suspended") {
        void this.audioContext.resume();
      }

      this.setState({ playing: true });

      // If seeking, queue play to resume after seek completes
      if (this._state.seeking) {
        this.resumeOnSeekComplete = true;

        return Promise.resolve();
      } else {
        return this.startPlaybackFromCurrentPosition();
      }
    });
  }

  /**
   * Pause playback.
   */
  pause(): void {
    if (!this._state.playing) return;
    if (!this._state.ready) throw new Error("Engine not loaded");

    this.logCommand("pause", { at: this._tickState.currentTimestamp }, () => {
      this.resumeOnSeekComplete = false;
      this.invalidatePendingPlayStart();
      this.getPlaybackBackend().pause();

      // Calculate current position using audio clock (scaled by playback rate)
      if (this.startedAt !== null && this.audioContext) {
        this.playingFrom = this.playingFrom + (this.audioContext.currentTime - this.startedAt) * this._state.playbackRate;
      }
      this.startedAt = null;

      this.stopTimestampLoop();
      this.setState({ playing: false });
      this.setTickState({ currentTimestamp: this.playingFrom });
    });
  }

  /**
   * Stop playback and return to beginning.
   */
  stop(): void {
    this.commandLog?.add("stop");
    this.pause();
    this.seek(0);
  }

  /**
   * Seek to a specific timestamp.
   * If currently playing, will resume after seek completes.
   */
  async seek(timestamp: number): Promise<void> {
    if (!this._state.ready) throw new Error("Engine not loaded");

    // Clamp timestamp to valid range
    const clampedTimestamp = clamp(timestamp, 0, this._state.duration);

    // Short circuit if not playing and already at position
    if (!this._state.playing && this.playingFrom === clampedTimestamp) return;

    const before = this._state;

    // If seeking while another seek is in progress, remember to resume if needed
    this.resumeOnSeekComplete = this.resumeOnSeekComplete || this._state.playing;

    // Pause if playing
    if (this._state.playing && !this._state.seeking) {
      this.invalidatePendingPlayStart();
      this.getPlaybackBackend().pause();
      this.startedAt = null;
      this.resumeOnSeekComplete = true;
      this.stopTimestampLoop();
    }

    // Update position
    this.playingFrom = clampedTimestamp;

    this.setState({
      seeking: true,
      playing: this.resumeOnSeekComplete,
    });
    this.setTickState({ currentTimestamp: clampedTimestamp });

    // Create seek promise
    const seekPromise = this.getPlaybackBackend().seek(clampedTimestamp);

    // Handle interruptible seek
    if (this.seekInterruptible) {
      this.seekInterruptible.replace(seekPromise);
    } else {
      this.seekInterruptible = new Interruptible(seekPromise);
    }

    // Wait for seek to complete (or be interrupted)
    try {
      await this.seekInterruptible!.promise;
      this.completeSeek();

      // Log after seek completes
      this.commandLog?.add("seek", { timestamp: clampedTimestamp }, before, this._state);
    } catch {
      // Seek was interrupted by another seek - do nothing
    }
  }

  private completeSeek(): void {
    this.seekInterruptible = null;

    if (this.resumeOnSeekComplete) {
      this.setState({
        seeking: false,
        playing: true,
      });
      void this.startPlaybackFromCurrentPosition();
    } else {
      this.setState({ seeking: false });
    }

    this.resumeOnSeekComplete = false;
  }

  /**
   * Seek by a relative delta from current position.
   */
  seekDelta(delta: number): void {
    this.commandLog?.add("seekDelta", { delta });
    const newTimestamp = clamp(
      this._tickState.currentTimestamp + delta,
      0,
      this._state.duration
    );
    this.seek(newTimestamp);
  }

  /**
   * Seek to a percentage of the duration.
   * @param percentage 0-1 value
   */
  seekPercentage(percentage: number): void {
    this.commandLog?.add("seekPercentage", { percentage });
    const newTimestamp = clamp(percentage, 0, 1) * this._state.duration;
    this.seek(newTimestamp);
  }

  /**
   * Wait for the engine to be ready.
   * Resolves immediately if already ready, otherwise waits for audio to be loaded.
   * Useful after adding tracks/regions to wait for audio to be loaded.
   */
  waitForReady(): Promise<void> {
    return this.playbackBackend?.ready ?? Promise.resolve();
  }

  // ============================================================================
  // Timestamp Loop
  // ============================================================================

  private startTimestampLoop(): void {
    const update = () => {
      // Check destroyed flag to prevent memory leaks from pending callbacks
      if (this.destroyed || !this._state.playing || this._state.seeking) {
        this.animationFrameId = null;
        return;
      }

      this.updateTimestamp();

      // Check if we've reached the end
      if (this._tickState.currentTimestamp >= this._state.duration) {
        this.stop();
        return;
      }

      this.animationFrameId = requestAnimationFrame(update);
    };

    this.animationFrameId = requestAnimationFrame(update);
  }

  private stopTimestampLoop(): void {
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }

  private updateTimestamp(): void {
    if (!this._state.playing || this._state.seeking || this.startedAt === null || !this.audioContext) {
      return;
    }

    let currentTimestamp = this.playingFrom + (this.audioContext.currentTime - this.startedAt) * this._state.playbackRate;

    // Clamp to duration
    if (currentTimestamp > this._state.duration) {
      currentTimestamp = this._state.duration;
    }

    // Only update if changed (avoid unnecessary rerenders)
    if (currentTimestamp !== this._tickState.currentTimestamp) {
      this.setTickState({ currentTimestamp });
    }
  }

  // ============================================================================
  // Gain Controls
  // ============================================================================

  /**
   * Set the playback speed.
   * If currently playing, pauses and resumes so that every
   * AudioBufferSourceNode is recreated with the new playbackRate.
   *
   * @param rate - Playback rate (e.g., 0.5, 0.75, 1.0, 1.25, 1.5, 2.0)
   */
  setPlaybackRate(rate: number): void {
    this.logCommand("setPlaybackRate", { rate }, () => {
      const wasPlaying = this._state.playing;

      if (wasPlaying) {
        // Snapshot the current position before pausing
        if (this.startedAt !== null && this.audioContext) {
          this.playingFrom = this.playingFrom + (this.audioContext.currentTime - this.startedAt) * this._state.playbackRate;
        }
        this.invalidatePendingPlayStart();
        this.getPlaybackBackend().pause();
        this.startedAt = null;
        this.stopTimestampLoop();
      }

      // Apply the new rate (stores rate + updates pitch shift node)
      this.getPlaybackBackend().setPlaybackRate(rate);
      this.setState({ playbackRate: rate });

      if (wasPlaying) {
        // Resume playback — new AudioBufferSourceNodes will use the new rate
        this.setState({ playing: true });
        void this.startPlaybackFromCurrentPosition();
      }

      this.schedulePersist();
    });
  }

  /**
   * Transpose pitch up or down by semitones.
   * Does not affect playback speed — only pitch.
   *
   * @param semitones - Semitone offset (e.g., -12 to +12, 0 = no transpose)
   */
  setTranspose(semitones: number): void {
    this.logCommand("setTranspose", { semitones }, () => {
      this.getPlaybackBackend().setTranspose(semitones);
      this.setState({ transpose: semitones });
      this.schedulePersist();
    });
  }

  /**
   * Set the master gain (0-1).
   */
  setMasterGain(gain: number): void {
    this.logCommand("setMasterGain", { gain }, () => {
      this.getPlaybackBackend().setMasterGain(gain);
      this.setState({ masterGain: gain });
      this.schedulePersist();
    });
  }

  /**
   * Set gain for a specific track (0-1).
   */
  setTrackGain(trackId: string, gain: number): void {
    this.logCommand("setTrackGain", { trackId, gain }, () => {
      this.updateTrackState(trackId, { gain });
    });
  }

  /**
   * Toggle mute for a track.
   * When muting a solo'd track, also clears solo.
   */
  toggleMute(trackId: string): void {
    const track = this._state.tracks[trackId];
    const newMuted = !(track?.muted ?? false);
    const updates: Partial<TrackState> = { muted: newMuted };
    // When muting a solo'd track, clear solo
    if (newMuted && track?.solo) {
      updates.solo = false;
    }
    this.logCommand("toggleMute", { trackId, muted: newMuted }, () => {
      this.updateTrackState(trackId, updates);
    });
  }

  /**
   * Toggle solo for a track.
   * When soloing a muted track, also clears mute.
   */
  toggleSolo(trackId: string): void {
    const track = this._state.tracks[trackId];
    const newSolo = !(track?.solo ?? false);
    const updates: Partial<TrackState> = { solo: newSolo };
    // When soloing a muted track, clear mute
    if (newSolo && track?.muted) {
      updates.muted = false;
    }
    this.logCommand("toggleSolo", { trackId, solo: newSolo }, () => {
      this.updateTrackState(trackId, updates);
    });
  }

  /**
   * Set label for a specific track.
   */
  setTrackLabel(trackId: string, label: string): void {
    this.logCommand("setTrackLabel", { trackId, label }, () => {
      this.updateTrackState(trackId, { label });
    });
  }

  /**
   * Reorder tracks by providing a new order of track IDs.
   * Each track's order property is updated to match its position in the array.
   */
  setTrackOrder(trackIds: string[]): void {
    this.logCommand("setTrackOrder", { trackIds }, () => {
      const newTracks = { ...this._state.tracks };
      trackIds.forEach((id, index) => {
        if (newTracks[id]) {
          newTracks[id] = { ...newTracks[id], order: index };
        }
      });
      this._state = { ...this._state, tracks: newTracks };
      this.notifyListeners();
    });
  }

  /**
   * Update track state and recompute disabled status for all tracks.
   */
  private updateTrackState(trackId: string, updates: Partial<TrackState>): void {
    // Get initial global solo state
    const hadGlobalSolo = Object.values(this._state.tracks).some((c) => c.solo);

    // Update the target track
    const currentTrack = this._state.tracks[trackId];
    if (!currentTrack) {
      throw new Error(`Track ${trackId} not found`);
    }
    const updatedTrack = { ...currentTrack, ...updates };

    // Compute new tracks object
    const newTracks = { ...this._state.tracks, [trackId]: updatedTrack };

    // Check new global solo state
    const hasGlobalSolo = Object.values(newTracks).some((c) => c.solo);

    // If global solo state changed, update all tracks; otherwise just the target
    if (hadGlobalSolo !== hasGlobalSolo) {
      this.recomputeAllTrackDisabledState(newTracks);
    } else {
      // Just update the target track
      newTracks[trackId].disabled =
        updatedTrack.muted || (hasGlobalSolo && !updatedTrack.solo);
      this.applyTrackGain(trackId, newTracks[trackId]);
    }

    // Update state
    this._state = { ...this._state, tracks: newTracks };
    this.notifyListeners();
    this.schedulePersist();
  }

  private applyTrackGain(trackId: string, state: TrackState): void {
    const effectiveGain = state.disabled ? 0 : state.gain;
    this.getPlaybackBackend().setTrackGain(trackId, effectiveGain);
  }

  /**
   * Recalculate disabled state for all tracks based on solo/mute state.
   * A track is disabled if it's muted OR if any track is solo'd and this one isn't.
   * Also applies the resulting gains.
   */
  private recomputeAllTrackDisabledState(tracks: Record<string, TrackState>): void {
    const hasGlobalSolo = Object.values(tracks).some((c) => c.solo);

    Object.keys(tracks).forEach((id) => {
      const track = tracks[id];
      const disabled = track.muted || (hasGlobalSolo && !track.solo);
      tracks[id] = { ...track, disabled };
      this.applyTrackGain(id, tracks[id]);
    });
  }

  /**
   * Calculate duration from regions.
   * Duration is the maximum end time (position + duration) of all regions.
   */
  private calculateDurationFromRegions(regions: Record<string, RegionState>): number {
    const regionList = Object.values(regions);
    if (regionList.length === 0) return 0;
    return Math.max(...regionList.map(r => r.position + r.duration));
  }

  // ============================================================================
  // Track Management
  // ============================================================================

  /**
   * Add tracks and regions to the engine.
   * Note: Should be called while playback is stopped.
   */
  async addTracks(tracks: TrackInfo[], regions: RegionData[] = []): Promise<void> {
    await this.logCommand("addTracks", { trackIds: tracks.map(c => c.id) }, async () => {
      if (this.destroyed) return;

      this.mergeRegionDebugState(
        Object.fromEntries(
          regions.map((region) => [
            region.id,
            {
              channelCount: region.channelCount ?? null,
              codec: region.codec ?? null,
              hasAudioBlob: region.audioBlob instanceof Blob,
              hasWaveformKey: region.waveformKey !== null,
            } satisfies Partial<AudioEngineRegionDebugState>,
          ])
        )
      );

      // Load regions using RegionLoader if any
      let loadedRegions: LoadedRegion[] = [];
      let loadedWaveforms: Record<string, WaveformData> = {};

      if (regions.length > 0 && this.audioContext && this.audioStorage) {
        const regionLoader = new LegacyRegionLoader(this.audioContext, this.audioStorage);
        const loadResults = await regionLoader.loadAll(
          regions.map((r) => ({
            audioBlob: r.audioBlob,
            id: r.id,
            key: r.key,
            waveformKey: r.waveformKey,
          })),
        );

        // Collect waveforms
        for (const result of loadResults) {
          loadedWaveforms[result.id] = result.waveform;
        }

        // Build LoadedRegion array
        loadedRegions = loadResults.map((result) => {
          const regionInfo = regions.find((r) => r.id === result.id)!;
          return {
            audioBlob: result.audioBlob ?? regionInfo.audioBlob,
            id: result.id,
            trackId: regionInfo.trackId,
            audioBuffer: result.audioBuffer,
            codec: regionInfo.codec,
            duration: result.duration ?? regionInfo.duration,
            position: regionInfo.position,
          };
        });
        this.updateLoadedRegionDebugState(loadedRegions);
      }

      // Add to the active playback backend (it will manage its own ready state)
      this.getPlaybackBackend().addTracks({
        tracks,
        regions: loadedRegions,
      });

      // Add track states
      const newTracks = { ...this._state.tracks };
      tracks.forEach((track) => {
        newTracks[track.id] = {
          id: track.id,
          label: track.label,
          order: track.order,
          ...DEFAULT_TRACK_STATE_VALUES,
        };
      });

      // Add region states
      const newRegions = { ...this._state.regions };
      regions.forEach((region) => {
        newRegions[region.id] = {
          id: region.id,
          trackId: region.trackId,
          position: region.position,
          duration: region.duration ?? 0,
          pendingUpload: false,
        };
      });

      const newDuration = this.calculateDurationFromRegions(newRegions);
      this._state = { ...this._state, tracks: newTracks, regions: newRegions, duration: newDuration };

      // Batch update waveforms
      if (Object.keys(loadedWaveforms).length > 0) {
        this.setState({ waveforms: { ...this._state.waveforms, ...loadedWaveforms } });
      }

      // Apply gains to new tracks
      tracks.forEach((track) => {
        this.applyTrackGain(track.id, newTracks[track.id]);
      });

      this.notifyListeners();

      // Note: Ready state is automatically synced via backend.onReadyStateChange listener
    });
  }

  /**
   * Add a new empty track to the engine.
   * Used for creating tracks for recording or file uploads.
   * @returns The new track ID
   */
  addTrack(label: string): string {
    const id = uuid();
    const order = this.getNextTrackOrder();

    this.logCommand("addTrack", { id, label, order }, () => {
      // Add empty track to the active playback backend
      this.getPlaybackBackend().addEmptyTrack(id);

      // Add track state
      const newTracks = {
        ...this._state.tracks,
        [id]: {
          id,
          label,
          order,
          ...DEFAULT_TRACK_STATE_VALUES,
        },
      };

      this._state = { ...this._state, tracks: newTracks };

      // Apply gain to new track
      this.applyTrackGain(id, newTracks[id]);

      this.notifyListeners();
    });

    return id;
  }

  /**
   * Get the next order value for a new track.
   */
  private getNextTrackOrder(): number {
    const maxOrder = Math.max(0, ...Object.values(this._state.tracks).map(c => c.order));
    return maxOrder + 1;
  }

  /**
   * Remove a track from the engine.
   */
  removeTrack(trackId: string): void {
    this.logCommand("removeTrack", { trackId }, () => {
      this.getPlaybackBackend().removeTrack(trackId);

      // Check global solo state before removal
      const hadGlobalSolo = Object.values(this._state.tracks).some((c) => c.solo);

      const newTracks = { ...this._state.tracks };
      delete newTracks[trackId];

      // Remove all regions belonging to this track
      const newRegions = { ...this._state.regions };
      const newWaveforms = { ...this._state.waveforms };
      const pendingRegionIds: string[] = [];

      Object.values(this._state.regions)
        .filter(r => r.trackId === trackId)
        .forEach(region => {
          delete newRegions[region.id];
          delete newWaveforms[region.id];
          // Track pending regions for storage cleanup
          if (region.pendingUpload) {
            this.pendingBlobs.delete(region.id);
            pendingRegionIds.push(region.id);
          }
        });

      // Recalculate global solo state after removal
      const hasGlobalSolo = Object.values(newTracks).some((c) => c.solo);

      // If global solo state changed, update disabled state for all remaining tracks
      if (hadGlobalSolo !== hasGlobalSolo) {
        this.recomputeAllTrackDisabledState(newTracks);
      }

      // Recalculate duration after removing regions
      const newDuration = this.calculateDurationFromRegions(newRegions);

      this.setState({ tracks: newTracks, regions: newRegions, waveforms: newWaveforms, duration: newDuration });

      // Remove pending blobs from storage
      if (this.engineStorage && pendingRegionIds.length > 0) {
        this.engineStorage.removePendingBlobs(pendingRegionIds).catch(console.error);
      }
    });
  }

  /**
   * Add a single region to an existing track.
   * Used when uploading audio to an empty track.
   *
   * When `audioBlob` and `waveformData` are provided on the region, the engine
   * decodes the blob locally and uses the waveform directly, skipping the
   * download from remote storage. This avoids a redundant network round-trip
   * when the caller already has the data in memory (e.g. after transcoding).
   *
   * @throws Error if duration is not provided
   */
  async addRegion(trackId: string, region: RegionData): Promise<void> {
    if (region.duration === undefined) {
      throw new Error("Duration is required when adding a region");
    }

    await this.logCommand("addRegion", { trackId, regionId: region.id }, async () => {
      if (this.destroyed) return;

      this.mergeRegionDebugState({
        [region.id]: {
          channelCount: region.channelCount ?? null,
          codec: region.codec ?? null,
          hasAudioBlob: region.audioBlob instanceof Blob,
          hasWaveformKey: region.waveformKey !== null,
        },
      });

      if (!this.audioContext || !this.audioStorage) {
        throw new Error("AudioEngine not loaded");
      }

      let loadResult: { audioBlob: Blob; audioBuffer: AudioBuffer; duration: number; waveform: WaveformData };

      if (region.audioBlob && region.waveformData) {
        // Use pre-loaded data — decode the blob locally, skip remote download
        const arrayBuffer = await region.audioBlob.arrayBuffer();
        const audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);
        loadResult = { audioBlob: region.audioBlob, audioBuffer, duration: audioBuffer.duration, waveform: region.waveformData };
      } else {
        // Fall back to downloading from remote storage
        const regionLoader = new LegacyRegionLoader(this.audioContext, this.audioStorage);
        loadResult = await regionLoader.loadFromKey(
          region.id,
          region.key,
          region.waveformKey
        );
      }

      this.updateLoadedRegionDebugState([
        {
          id: region.id,
          trackId,
          audioBlob: loadResult.audioBlob,
          audioBuffer: loadResult.audioBuffer,
          codec: region.codec,
          duration: loadResult.duration,
          position: region.position,
        },
      ]);

      // Add region to the active playback backend (it will manage its own ready state)
      this.getPlaybackBackend().addRegionToTrack(trackId, {
        audioBlob: loadResult.audioBlob,
        id: region.id,
        trackId,
        audioBuffer: loadResult.audioBuffer,
        codec: region.codec,
        duration: loadResult.duration,
        position: region.position,
      });

      // Add region to state
      const newRegions = {
        ...this._state.regions,
        [region.id]: {
          id: region.id,
          trackId,
          position: region.position,
          duration: region.duration!,
          pendingUpload: false,
        },
      };

      const newDuration = this.calculateDurationFromRegions(newRegions);

      // Update state with region and waveform
      this.setState({
        regions: newRegions,
        duration: newDuration,
        waveforms: { ...this._state.waveforms, [region.id]: loadResult.waveform },
      });

      // Note: Ready state is automatically synced via backend.onReadyStateChange listener
    });
  }

  /**
   * Set the recording target track.
   * When set, the next recording will go into this track.
   */
  setRecordingTarget(trackId: string | null): void {
    this.logCommand("setRecordingTarget", { trackId }, () => {
      this.setState({ recordingTargetTrackId: trackId });
    });
  }

  // ============================================================================
  // Recording
  // ============================================================================

  /**
   * Set the selected input device for recording.
   * This is persisted and will be used when starting recording.
   */
  setSelectedInputDevice(deviceId: string | null): void {
    this.logCommand("setSelectedInputDevice", { deviceId }, () => {
      this.setState({ selectedInputDeviceId: deviceId });
      this.schedulePersist();
    });
  }

  /**
   * Get list of available audio input devices.
   * Device labels will only be available after permission is granted.
   */
  async getInputDevices(): Promise<AudioInputDevice[]> {
    return this.inputManager.getDevices();
  }

  /**
   * Request microphone permission.
   * This will show the browser's permission prompt if not already granted.
   */
  async requestInputPermission(): Promise<void> {
    return this.inputManager.requestPermission();
  }

  /**
   * Check if microphone permission has been granted.
   */
  async hasInputPermission(): Promise<boolean> {
    return this.inputManager.hasPermission();
  }

  /**
   * Start monitoring the input level from the selected device.
   * The input level will be updated in state at ~60fps.
   * If already monitoring, restarts with the current selected device.
   *
   * @throws Error if no input device has been selected
   */
  async startInputMonitoring(): Promise<void> {
    if (!this._state.selectedInputDeviceId) throw new Error("No input device selected");
    if (!this.audioContext) throw new Error("AudioContext not available");

    const before = this._state;

    // Start the input monitor (handles cleanup of any existing monitoring)
    await this.audioInput.start(
      this.audioContext,
      this._state.selectedInputDeviceId,
      this._state.selectedInputChannel,
      this._state.inputAudioMonitoring,
      (levels) => {
        // Only update if level changed significantly (avoid excessive rerenders)
        const roundedLeft = Math.round(levels.left * 100) / 100;
        const roundedRight = Math.round(levels.right * 100) / 100;
        if (roundedLeft !== this._tickState.inputLevel || roundedRight !== this._tickState.inputLevelRight) {
          this.setTickState({ inputLevel: roundedLeft, inputLevelRight: roundedRight });
        }
      }
    );

    this.setState({
      inputMonitoring: true,
      inputChannelCount: this.audioInput.channelCount,
    });
    this.setTickState({
      inputLevel: 0,
      inputLevelRight: 0,
    });

    this.commandLog?.add("startInputMonitoring", { deviceId: this._state.selectedInputDeviceId }, before, this._state);
  }

  /**
   * Stop monitoring the input level.
   */
  stopInputMonitoring(): void {
    if (!this.audioInput.isActive) return;

    this.logCommand("stopInputMonitoring", undefined, () => {
      this.audioInput.stop();

      this.setState({
        inputMonitoring: false,
        inputChannelCount: null,
      });
      this.setTickState({
        inputLevel: 0,
        inputLevelRight: 0,
      });
    });
  }

  /**
   * Enable or disable input audio monitoring (hear yourself through speakers).
   * This only works when input level monitoring is active.
   *
   * @param enabled - Whether to route input audio to speakers
   */
  setInputAudioMonitoring(enabled: boolean): void {
    this.logCommand("setInputAudioMonitoring", { enabled }, () => {
      this.audioInput.setAudioMonitoring(enabled);
      this.setState({ inputAudioMonitoring: enabled });
      this.schedulePersist();
    });
  }

  /**
   * Set the selected input channel for recording and monitoring.
   * Only applicable when the input device has multiple tracks.
   */
  setSelectedInputChannel(channel: 'left' | 'right' | 'stereo'): void {
    this.logCommand("setSelectedInputChannel", { channel }, () => {
      const wasMonitoring = this._state.inputMonitoring;
      this.setState({ selectedInputChannel: channel });
      this.schedulePersist();

      // Restart input monitoring to apply new track routing
      if (wasMonitoring) {
        this.startInputMonitoring().catch(() => {
          // Ignore errors - monitoring will just stop
        });
      }
    });
  }

  /**
   * Start recording from microphone into the specified track.
   * Recording will begin from current playback position.
   * Automatically starts playback if not already playing.
   *
   * @param targetTrackId - The track to record into (required)
   */
  async startRecording(targetTrackId: string): Promise<void> {
    if (this._state.recording) return;
    if (!this._state.ready) throw new Error("Engine not loaded");
    if (!this.audioContext) throw new Error("AudioContext not available");
    if (!this._state.selectedInputDeviceId) throw new Error("No input device selected");
    if (!targetTrackId) throw new Error("targetTrackId is required - call addTrack() first for new tracks");
    if (!this._state.tracks[targetTrackId]) throw new Error(`Track ${targetTrackId} not found`);

    const before = this._state;

    // Ensure input monitoring is active (reuse existing or start new)
    if (!this.audioInput.isActive) {
      await this.startInputMonitoring();
    }

    // Start playback first if not already playing (before setting recording state)
    // This avoids a race condition where the UI sees recording=true but playing=false
    // and immediately stops the recording
    if (!this._state.playing) {
      await this.play();
    }

    // Start recording via input monitor and wait for worklet confirmation.
    // The worklet confirms when recording actually starts (in its process() callback),
    // returning the exact frame number when capture began.
    const { startFrame } = await this.audioInput.startRecording((waveform) => {
      this.setTickState({ recordingWaveform: waveform });
    });

    // Convert the worklet's startFrame to a song position.
    //
    // Both startFrame and startedAt are in the audio clock domain
    // (audioContext.currentTime / frame count), so this calculation stays entirely
    // within one timing domain, avoiding inconsistencies from mixing performance.now()
    // with audio timing.
    //
    // The calculation:
    // - startFrame: frame number when recording started (audio clock)
    // - startedAt: audioContext.currentTime when playback started
    // - playingFrom: song position when playback started
    //
    // Song position = playingFrom + (time since playback started in audio clock)
    const sampleRate = this.audioContext.sampleRate;
    const recordingStartContextTime = startFrame / sampleRate;
    const timeSincePlaybackStarted = recordingStartContextTime - this.startedAt!;
    let position = this.playingFrom + timeSincePlaybackStarted * this._state.playbackRate;

    // Compensate for audio latency when recording speaker output (e.g., recording
    // other tracks into a new track). The total latency includes:
    // - baseLatency: processing latency of the audio graph (applies to both input and output)
    // - outputLatency: hardware output latency (time for audio to reach speakers)
    const baseLatency = this.audioContext.baseLatency;
    const outputLatency = this.audioContext.outputLatency ?? 0;
    const totalLatency = baseLatency + outputLatency;
    position = Math.max(0, position - totalLatency);

    // Silence the target track so we don't hear existing audio while recording
    // (directly set gain to 0, without affecting the visible muted state)
    this.getPlaybackBackend().setTrackGain(targetTrackId, 0);

    // Update state after playback has started
    this.setState({
      recording: true,
      recordingStartPosition: position,
      recordingTargetTrackId: targetTrackId,
    });

    this.commandLog?.add("startRecording", { position, targetTrackId, startFrame, recordingStartContextTime, startedAt: this.startedAt, timeSincePlaybackStarted, baseLatency, outputLatency, totalLatency }, before, this._state);
  }

  /**
   * Stop recording and add the recorded audio as a region to the target track.
   * Handles overlapping regions automatically (splits/trims as needed).
   */
  async stopRecording(): Promise<PendingRecording> {
    if (!this.audioInput.isRecording || !this._state.recording) {
      throw new Error("Not recording");
    }
    if (!this.audioContext) throw new Error("AudioContext not available");

    const before = this._state;
    const startPosition = this._state.recordingStartPosition!;
    const targetTrackId = this._state.recordingTargetTrackId!;

    // Stop the recorder and get the audio buffer directly (no decoding needed)
    const { audioBuffer } = await this.audioInput.stopRecording();

    // Calculate actual duration from the audio buffer
    const duration = audioBuffer.duration;
    const endPosition = startPosition + duration;

    // Generate ID for the new region
    const regionId = uuid();

    // Create WAV blob for upload
    const blob = audioBufferToWav(audioBuffer);

    // Store the blob for later upload
    this.pendingBlobs.set(regionId, blob);

    // Apply punch-in to handle any overlapping regions and add the new one
    const punchInResult = this.applyPunchIn(targetTrackId, regionId, audioBuffer, startPosition, endPosition);

    // Add the new region to state
    const newRegions = { ...this._state.regions };
    newRegions[regionId] = {
      id: regionId,
      trackId: targetTrackId,
      position: startPosition,
      duration,
      pendingUpload: true,
    };

    // Also mark any created regions (from splitting) as pending
    for (const createdRegion of punchInResult.createdRegions) {
      newRegions[createdRegion.id] = {
        id: createdRegion.id,
        trackId: targetTrackId,
        position: createdRegion.position,
        duration: createdRegion.duration,
        pendingUpload: true,
      };
      // Store blobs for created regions too
      const createdBlob = audioBufferToWav(createdRegion.audioBuffer);
      this.pendingBlobs.set(createdRegion.id, createdBlob);
    }

    // Track deleted regions - only persist those that were saved (not pending)
    const newDeletedRegionIds = [...this._state.deletedRegionIds];
    const deletedPendingRegionIds: string[] = [];

    // Remove deleted regions from state
    for (const deletedId of punchInResult.deletedRegionIds) {
      const deletedRegion = this._state.regions[deletedId];
      if (deletedRegion) {
        // If this was a pending region, remove its blob from memory and track for storage cleanup
        if (deletedRegion.pendingUpload) {
          this.pendingBlobs.delete(deletedId);
          deletedPendingRegionIds.push(deletedId);
        } else {
          // If it was a saved region, track it for database deletion
          newDeletedRegionIds.push(deletedId);
        }
      }
      delete newRegions[deletedId];
    }

    // Compute waveforms for the new recording region and any regions created by punch-in splitting
    const newWaveforms = { ...this._state.waveforms };
    newWaveforms[regionId] = RegionLoader.calculateWaveform(audioBuffer);
    for (const createdRegion of punchInResult.createdRegions) {
      newWaveforms[createdRegion.id] = RegionLoader.calculateWaveform(createdRegion.audioBuffer);
    }

    // Update state with recalculated duration
    const newDuration = this.calculateDurationFromRegions(newRegions);
    this._state = {
      ...this._state,
      regions: newRegions,
      waveforms: newWaveforms,
      duration: newDuration,
      recording: false,
      recordingStartPosition: null,
      deletedRegionIds: newDeletedRegionIds,
    };

    this.notifyListeners();
    this.setTickState({ recordingWaveform: null });

    // Restore the target track's gain (was silenced during recording)
    const trackState = this._state.tracks[targetTrackId];
    if (trackState) {
      this.applyTrackGain(targetTrackId, trackState);
    }

    this.commandLog?.add("stopRecording", { regionId, position: startPosition, duration, targetTrackId }, before, this._state);

    // Remove deleted pending blobs from storage
    if (this.engineStorage && deletedPendingRegionIds.length > 0) {
      this.engineStorage.removePendingBlobs(deletedPendingRegionIds).catch(console.error);
    }

    // Persist the pending blobs and state
    this.persistPendingBlobs().catch(console.error);
    this.schedulePersist();

    return {
      id: regionId,
      blob,
      position: startPosition,
      duration,
      punchIn: punchInResult,
    };
  }

  /**
   * Apply punch-in recording to an existing track.
   * Splits/trims overlapping regions and inserts the new recording.
   * Returns info about affected regions for persistence.
   */
  private applyPunchIn(
    trackId: string,
    regionId: string,
    audioBuffer: AudioBuffer,
    startPosition: number,
    endPosition: number
  ): PunchInResult {
    if (!this._state.tracks[trackId]) {
      throw new Error(`Track ${trackId} not found`);
    }

    const playbackBackend = this.getPlaybackBackend();

    // Track affected regions for the result
    const deletedRegionIds: string[] = [];
    const createdRegions: CreatedRegion[] = [];

    // Find all regions that overlap with the punch-in range
    const overlappingRegions = playbackBackend.getRegionsInRange(
      trackId,
      startPosition,
      endPosition
    );

    for (const region of overlappingRegions) {
      const regionEnd = region.position + region.duration;

      // Case 1: Region completely contained within punch range - remove it
      if (region.position >= startPosition && regionEnd <= endPosition) {
        playbackBackend.removeRegionFromTrack(trackId, region.id);
        deletedRegionIds.push(region.id);
        // Remove from waveforms
        const newWaveforms = { ...this._state.waveforms };
        delete newWaveforms[region.id];
        this._state = { ...this._state, waveforms: newWaveforms };
        continue;
      }

      // Case 2: Punch is in the middle of region - split it
      if (region.position < startPosition && regionEnd > endPosition) {
        const originalBuffer = playbackBackend.getRegionBuffer(trackId, region.id);
        if (originalBuffer) {

          // Calculate "before" piece
          const beforeDuration = startPosition - region.position;
          const beforeBuffer = sliceAudioBuffer(
            originalBuffer,
            0,
            beforeDuration,
            this.audioContext!
          );
          const beforeId = uuid();

          // Calculate "after" piece
          const afterDuration = regionEnd - endPosition;
          const afterOffset = endPosition - region.position;
          const afterBuffer = sliceAudioBuffer(
            originalBuffer,
            afterOffset,
            afterDuration,
            this.audioContext!
          );
          const afterId = uuid();

          // Mark original for deletion
          deletedRegionIds.push(region.id);
          playbackBackend.removeRegionFromTrack(trackId, region.id);

          // Add the "before" region
          playbackBackend.addRegionToTrack(trackId, {
            id: beforeId,
            trackId,
            audioBuffer: beforeBuffer,
            codec: null,
            position: region.position,
          });
          createdRegions.push({
            id: beforeId,
            position: region.position,
            duration: beforeDuration,
            audioBuffer: beforeBuffer,
          });

          // Add the "after" region
          playbackBackend.addRegionToTrack(trackId, {
            id: afterId,
            trackId,
            audioBuffer: afterBuffer,
            codec: null,
            position: endPosition,
          });
          createdRegions.push({
            id: afterId,
            position: endPosition,
            duration: afterDuration,
            audioBuffer: afterBuffer,
          });
        }
        continue;
      }

      // Case 3: Punch starts inside region (overlaps end) - truncate
      if (region.position < startPosition && regionEnd > startPosition) {
        const originalBuffer = playbackBackend.getRegionBuffer(trackId, region.id);
        if (originalBuffer) {
          const newDuration = startPosition - region.position;
          const newBuffer = sliceAudioBuffer(
            originalBuffer,
            0,
            newDuration,
            this.audioContext!
          );
          const newId = uuid();

          // Mark original for deletion and remove
          deletedRegionIds.push(region.id);
          playbackBackend.removeRegionFromTrack(trackId, region.id);

          // Add truncated region with new ID
          playbackBackend.addRegionToTrack(trackId, {
            id: newId,
            trackId,
            audioBuffer: newBuffer,
            codec: null,
            position: region.position,
          });
          createdRegions.push({
            id: newId,
            position: region.position,
            duration: newDuration,
            audioBuffer: newBuffer,
          });
        }
        continue;
      }

      // Case 4: Punch ends inside region (overlaps start) - trim start
      if (region.position < endPosition && regionEnd > endPosition) {
        const originalBuffer = playbackBackend.getRegionBuffer(trackId, region.id);
        if (originalBuffer) {
          const trimAmount = endPosition - region.position;
          const newDuration = region.duration - trimAmount;
          const newBuffer = sliceAudioBuffer(
            originalBuffer,
            trimAmount,
            newDuration,
            this.audioContext!
          );
          const newId = uuid();

          // Mark original for deletion and remove
          deletedRegionIds.push(region.id);
          playbackBackend.removeRegionFromTrack(trackId, region.id);

          // Add trimmed region with new ID
          playbackBackend.addRegionToTrack(trackId, {
            id: newId,
            trackId,
            audioBuffer: newBuffer,
            codec: null,
            position: endPosition,
          });
          createdRegions.push({
            id: newId,
            position: endPosition,
            duration: newDuration,
            audioBuffer: newBuffer,
          });
        }
        continue;
      }
    }

    // Add the new punch-in region
    playbackBackend.addRegionToTrack(trackId, {
      id: regionId,
      trackId,
      audioBuffer,
      codec: null,
      position: startPosition,
    });

    // Waveforms for the new region and any created regions are computed
    // by the caller (stopRecording) after applyPunchIn returns.

    return {
      trackId,
      deletedRegionIds,
      createdRegions,
    };
  }

  /**
   * Cancel recording without saving.
   */
  cancelRecording(): void {
    this.logCommand("cancelRecording", undefined, () => {
      if (this.audioInput.isRecording) {
        this.audioInput.cancelRecording();
      }

      // Restore recording target track's gain if it was silenced
      const recordingTargetTrackId = this._state.recordingTargetTrackId;
      if (recordingTargetTrackId) {
        const trackState = this._state.tracks[recordingTargetTrackId];
        if (trackState) {
          this.applyTrackGain(recordingTargetTrackId, trackState);
        }
      }

      this.setState({
        recording: false,
        recordingStartPosition: null,
      });
      this.setTickState({ recordingWaveform: null });
    });
  }

  /**
   * Get the blob for a pending region (for upload).
   */
  getPendingBlob(regionId: string): Blob | undefined {
    return this.pendingBlobs.get(regionId);
  }

  /**
   * Check if a track has any pending regions.
   */
  hasTrackPendingRegions(trackId: string): boolean {
    return Object.values(this._state.regions).some(
      r => r.trackId === trackId && r.pendingUpload
    );
  }

  /**
   * Mark a region as saved (no longer pending).
   * Called after successfully uploading the region to storage.
   */
  markRegionSaved(regionId: string): void {
    this.logCommand("markRegionSaved", { regionId }, () => {
      const region = this._state.regions[regionId];
      if (!region) return;

      // Update region state
      const newRegions = {
        ...this._state.regions,
        [regionId]: {
          ...region,
          pendingUpload: false,
        },
      };

      this._state = { ...this._state, regions: newRegions };

      // Remove pending blob from memory
      this.pendingBlobs.delete(regionId);

      // Remove pending blob from storage
      if (this.engineStorage) {
        this.engineStorage.removePendingBlob(regionId).catch(console.error);
      }

      this.notifyListeners();
      this.schedulePersist();
    });
  }

  /**
   * Discard all pending regions for a track.
   * Called when user wants to revert changes.
   */
  discardTrackPendingRegions(trackId: string): void {
    this.logCommand("discardTrackPendingRegions", { trackId }, () => {
      const pendingRegions = Object.values(this._state.regions).filter(
        r => r.trackId === trackId && r.pendingUpload
      );

      if (pendingRegions.length === 0) return;

      const newRegions = { ...this._state.regions };
      const newWaveforms = { ...this._state.waveforms };
      const regionIdsToRemove: string[] = [];

      for (const region of pendingRegions) {
        this.getPlaybackBackend().removeRegionFromTrack(trackId, region.id);

        // Remove from state
        delete newRegions[region.id];
        delete newWaveforms[region.id];

        // Remove pending blob from memory
        this.pendingBlobs.delete(region.id);

        // Track region ID for storage cleanup
        regionIdsToRemove.push(region.id);
      }

      // Recalculate duration after removing regions
      const newDuration = this.calculateDurationFromRegions(newRegions);

      this._state = { ...this._state, regions: newRegions, waveforms: newWaveforms, duration: newDuration };
      this.notifyListeners();

      // Remove pending blobs from storage
      if (this.engineStorage && regionIdsToRemove.length > 0) {
        this.engineStorage.removePendingBlobs(regionIdsToRemove).catch(console.error);
      }

      this.schedulePersist();
    });
  }

  /**
   * Get region IDs that were deleted by punch-in and need to be removed from database.
   */
  getDeletedRegionIds(): string[] {
    return this._state.deletedRegionIds;
  }

  /**
   * Clear deleted region IDs after they've been removed from database.
   * Can optionally pass specific IDs to clear, or clear all.
   */
  clearDeletedRegionIds(ids?: string[]): void {
    this.logCommand("clearDeletedRegionIds", { ids }, () => {
      if (ids) {
        const newDeletedIds = this._state.deletedRegionIds.filter(id => !ids.includes(id));
        this._state = { ...this._state, deletedRegionIds: newDeletedIds };
      } else {
        this._state = { ...this._state, deletedRegionIds: [] };
      }
      this.notifyListeners();
      this.schedulePersist();
    });
  }

  /**
   * Get all regions for a track.
   */
  getRegionsForTrack(trackId: string): RegionState[] {
    return Object.values(this._state.regions).filter(r => r.trackId === trackId);
  }

  // ============================================================================
  // Waveform Handling
  // ============================================================================

  /**
   * Set waveform data for a region.
   * Can be called externally to set waveform data.
   */
  setWaveform = (regionId: string, waveform: WaveformData): void => {
    // Only update if waveform has changed
    if (this._state.waveforms[regionId] === waveform) return;

    this.logCommand("setWaveform", { regionId }, () => {
      this._state = {
        ...this._state,
        waveforms: {
          ...this._state.waveforms,
          [regionId]: waveform,
        },
      };
      this.notifyListeners();
    });
  };

  /**
   * Get waveform data for a region.
   */
  getWaveform(regionId: string): WaveformData | undefined {
    return this._state.waveforms[regionId];
  }

  /**
   * Get a combined master waveform that merges all regions across all tracks.
   * The result is cached and only recomputed when waveforms, regions, or duration change.
   *
   * This is a static full-mix view — it does not account for gain, mute, or solo state.
   */
  getMasterWaveform(): WaveformData | null {
    const { waveforms, regions, duration } = this._state;

    // Return cached result if deps haven't changed (reference equality)
    if (
      this._masterWaveformDeps &&
      this._masterWaveformDeps.waveforms === waveforms &&
      this._masterWaveformDeps.regions === regions &&
      this._masterWaveformDeps.duration === duration
    ) {
      return this._cachedMasterWaveform;
    }

    this._cachedMasterWaveform = combineMasterWaveform(waveforms, regions, duration);
    this._masterWaveformDeps = { waveforms, regions, duration };
    return this._cachedMasterWaveform;
  }

  // ============================================================================
  // Persistence
  // ============================================================================

  /**
   * Restore pending regions from persisted storage.
   * Called during load() when there are pending regions in persisted state.
   */
  private async restorePendingRegions(
    audioContext: AudioContext,
    pendingRegions: PersistedPendingRegion[],
    tracks: Record<string, TrackState>,
    regions: Record<string, RegionState>,
    _waveforms: Record<string, WaveformData>
  ): Promise<void> {
    for (const pending of pendingRegions) {
      // Skip if track doesn't exist
      if (!tracks[pending.trackId]) continue;

      // Load blob from storage
      const blob = await this.engineStorage?.getPendingBlob(pending.id);
      if (!blob) continue;

      try {
        // Decode the blob to AudioBuffer
        const arrayBuffer = await blob.arrayBuffer();
        const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

        // Add to pendingBlobs map
        this.pendingBlobs.set(pending.id, blob);
        this.mergeRegionDebugState({
          [pending.id]: {
            channelCount: audioBuffer.numberOfChannels,
            codec: null,
            hasAudioBlob: false,
            hasAudioBuffer: true,
            hasWaveformKey: false,
          },
        });

        // Add to regions state
        regions[pending.id] = {
          id: pending.id,
          trackId: pending.trackId,
          position: pending.position,
          duration: pending.duration,
          pendingUpload: true,
        };

        // Add to the playback backend after the initial load completes.
        // We defer this because tracks are created as part of backend.load().
        // Store pending region data for later addition
        this.pendingRegionsToRestore.push({
          trackId: pending.trackId,
          regionId: pending.id,
          audioBuffer,
          position: pending.position,
        });
      } catch (error) {
        console.warn(`Failed to restore pending region ${pending.id}:`, error);
        // Clean up the blob from storage if we couldn't restore it
        await this.engineStorage?.removePendingBlob(pending.id);
      }
    }
  }

  /**
   * Persist the current state to storage.
   * Called automatically (debounced) when persistable state changes.
   */
  private async persistState(): Promise<void> {
    if (!this.engineStorage) return;

    // Collect pending regions
    const pendingRegions: PersistedPendingRegion[] = Object.values(this._state.regions)
      .filter(r => r.pendingUpload)
      .map(r => ({
        id: r.id,
        trackId: r.trackId,
        position: r.position,
        duration: r.duration,
      }));

    // Collect waveforms for pending regions
    const pendingWaveforms: Record<string, WaveformData> = {};
    for (const region of pendingRegions) {
      const waveform = this._state.waveforms[region.id];
      if (waveform) {
        pendingWaveforms[region.id] = waveform;
      }
    }

    const persistedState: PersistedEngineState = {
      masterGain: this._state.masterGain,
      tracks: Object.fromEntries(
        Object.entries(this._state.tracks).map(([id, state]) => [
          id,
          {
            solo: state.solo,
            muted: state.muted,
            gain: state.gain,
          },
        ])
      ),
      selectedInputDeviceId: this._state.selectedInputDeviceId,
      selectedInputChannel: this._state.selectedInputChannel,
      inputAudioMonitoring: this._state.inputAudioMonitoring,
      pendingRegions: pendingRegions.length > 0 ? pendingRegions : undefined,
      deletedRegionIds: this._state.deletedRegionIds.length > 0 ? this._state.deletedRegionIds : undefined,
      pendingWaveforms: Object.keys(pendingWaveforms).length > 0 ? pendingWaveforms : undefined,

      // Don't persist playbackRate or transpose, to prevent users accidentally leaving them on
    };

    await this.engineStorage.setState(persistedState);
  }

  /**
   * Persist pending blobs to storage.
   * Called when a new recording is stopped.
   */
  private async persistPendingBlobs(): Promise<void> {
    if (!this.engineStorage) return;

    // Persist all pending blobs
    const promises: Promise<void>[] = [];
    for (const [regionId, blob] of this.pendingBlobs.entries()) {
      promises.push(this.engineStorage.setPendingBlob(regionId, blob));
    }
    await Promise.all(promises);
  }

  /**
   * Schedule a persist operation (debounced).
   * Should be called after any change to persistable state.
   */
  private schedulePersist(): void {
    if (this.debouncedPersist) {
      this.debouncedPersist();
    }
  }
}
